import AsyncStorage from "@/platform/async-storage";

export type AppEnvironment = "colaborador" | "administrador" | "cliente" | "prestador";

export const ENVIRONMENT_CHOICE_DONE_KEY = "@tirvumais:environment-choice-done";
export const APP_ENVIRONMENT_KEY = "@tirvumais:app-environment";
export const PORTAL_HASH_KEY = "@tirvumais:portal-hash";
/** TirvuID informado nos botões Administrador/Cliente/Prestador da tela de seleção de ambiente.
 *  Fica ARMAZENADO para que, nas próximas aberturas (usuário NÃO-colaborador), o botão direcione
 *  direto ao ambiente sem pedir o TirvuID de novo. NÃO é removido no signOut (sobrevive ao logout
 *  do colaborador — só limpamos chaves AUTH_*). */
export const PORTAL_TIRVU_ID_KEY = "@tirvumais:portal-tirvu-id";
export const TIRVU_DATA_CONTENT_KEY = "@tirvumais:tirvu-data-content";
export const TIRVU_DATA_KEY_KEY = "@tirvumais:tirvu-data-key";
/** Usuário concluiu a tela de permissões (fluxo intro → permissões → ambiente). */
export const PERMISSIONS_COMPLETED_KEY = "@tirvumais:permissions-completed";
/** Botão flutuante de trocar acesso do PortalWeb: posição e "escondido" que o usuário escolheu.
 *  Prefixo — o PortalWeb acrescenta `:<ambiente>`, porque o lugar livre na tela do Administrador
 *  não é o mesmo da do Cliente. É preferência de tela, não de sessão. */
export const PORTAL_FAB_KEY = "@tirvumais:portal-fab";

export type PostSplashTarget =
  | "intro"
  | "permissions"
  | "environment-select"
  | "onboarding";

export async function saveColaboradorChoice(): Promise<void> {
  await AsyncStorage.multiSet([
    [APP_ENVIRONMENT_KEY, "colaborador"],
    [ENVIRONMENT_CHOICE_DONE_KEY, "true"],
  ]);
}

export async function savePortalChoice(
  environment: "administrador" | "cliente" | "prestador",
  hash: string
): Promise<void> {
  const trimmed = hash.trim();
  // 🔒 CROSS-TENANT (30/07): a sessão web espelhada (tirvuDataContent/tirvuDataKey)
  // pertence ao portal do hash ANTERIOR. O PortalWeb decide a rota pela mera
  // EXISTÊNCIA desses blobs (vai direto ao dashboard/ponto) — mantê-los ao trocar
  // de hash reabria a sessão da empresa anterior sob o portal da nova. Trocou o
  // hash → descarta a sessão web (o portal refaz o login do tenant certo).
  const hashAtual = (await AsyncStorage.getItem(PORTAL_HASH_KEY))?.trim() ?? "";
  if (hashAtual && hashAtual.toUpperCase() !== trimmed.toUpperCase()) {
    await AsyncStorage.multiRemove([
      TIRVU_DATA_CONTENT_KEY,
      TIRVU_DATA_KEY_KEY,
    ]).catch(() => {});
  }
  await AsyncStorage.multiSet([
    [APP_ENVIRONMENT_KEY, environment],
    [PORTAL_HASH_KEY, trimmed],
    [ENVIRONMENT_CHOICE_DONE_KEY, "true"],
  ]);
}

/** Armazena o TirvuID informado nos botões de ambiente (sempre UPPERCASE) para reuso. */
export async function savePortalTirvuId(tirvuId: string): Promise<void> {
  const id = tirvuId.trim().toUpperCase();
  if (!id) return;
  await AsyncStorage.setItem(PORTAL_TIRVU_ID_KEY, id);
}

/** Lê o TirvuID já informado antes (ou null). UPPERCASE. */
export async function getPortalTirvuId(): Promise<string | null> {
  const id = (await AsyncStorage.getItem(PORTAL_TIRVU_ID_KEY))?.trim();
  return id ? id.toUpperCase() : null;
}

/** Monta a URL do ambiente web (Administrador/Cliente/Prestador) a partir do TirvuID.
 *  TirvuID SEMPRE em UPPERCASE na URL. NUNCA usar ios/android.tirvumais (WebView legado). */
export function buildEnvironmentUrl(
  environment: "administrador" | "cliente" | "prestador",
  tirvuId: string,
): string {
  const id = tirvuId.toUpperCase();
  switch (environment) {
    case "administrador":
      return `https://tirvumais.com.br/${id}/Adm/Acesso`;
    case "cliente":
      return `https://tirvumais.com.br/${id}/Cliente/Acesso`;
    case "prestador":
      return `https://prestador.tirvumais.com.br/?hash=${id}`;
  }
}

export async function resolvePostSplashTarget(params: {
  introSeen: boolean;
  permissionsCompleted: boolean;
}): Promise<PostSplashTarget> {
  if (!params.introSeen) {
    return "intro";
  }

  if (!params.permissionsCompleted) {
    return "permissions";
  }

  // SEM colaborador autenticado (o isAuthenticated → Home é checado ANTES, em app/index), o app
  // SEMPRE abre na seleção de ambiente, com as OPÇÕES de acesso. Nada de auto-rota para
  // onboarding/login nem para WebView de portal: um colaborador deslogado também cai aqui e chega
  // ao login pelo botão "Colaborador"; admin/cliente/prestador, pelos respectivos botões.
  return "environment-select";
}
